import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Settings2, CreditCard, ArrowDown, TrendingUp } from 'lucide-react';
import { useCommunityMember } from '@/contexts/CommunityMemberContext';
import { usePublicCommunity } from '@/contexts/PublicCommunityContext';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import FeaturesManager from '@/components/admin/FeaturesManager';
import PlanFeaturesManager from '@/components/admin/PlanFeaturesManager';
import { usePlanHierarchy } from '@/hooks/useFeatures';
import { toast } from '@/components/ui/use-toast';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useIsMobile } from '@/hooks/use-mobile';

interface SubscriptionPlan {
  id: string;
  name: string;
  rank: number;
  is_active: boolean;
  stripe_product_id: string | null;
  community_id: string;
  created_at: string;
  updated_at: string;
}

const SubscriptionFeaturesManagement = () => {
  const { isAllowedTo, isCheckingAccess } = useCommunityMember();
  const { currentCommunity } = usePublicCommunity();
  const isMobile = useIsMobile();
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);

  // Check permission on component mount
  useEffect(() => {
    const checkPermission = async () => {
      if (!isCheckingAccess) {
        const allowed = await isAllowedTo('manage_features');
        setHasPermission(allowed);
        
        if (!allowed) {
          toast({
            title: "Access Denied",
            description: "You don't have permission to manage features.",
            variant: "destructive",
          });
        }
      }
    };
    
    checkPermission();
  }, [isAllowedTo, isCheckingAccess]);

  // Fetch subscription plans for this community
  const { data: plans, isLoading: plansLoading } = useQuery({
    queryKey: ['community-subscription-plans', currentCommunity.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('community_subscription_plans')
        .select('*')
        .eq('community_id', currentCommunity.id)
        .order('rank');

      if (error) {
        console.error('Error fetching subscription plans:', error);
        throw error;
      }

      return data as SubscriptionPlan[];
    },
    enabled: !!currentCommunity.id && hasPermission === true,
  });

  // Get plan hierarchy with inheritance information
  const { data: planHierarchy, isLoading: hierarchyLoading } = usePlanHierarchy(
    hasPermission === true ? currentCommunity.id : undefined
  );

  // Show loading while checking permissions
  if (isCheckingAccess || hasPermission === null) {
    return (
      <div className="flex justify-center py-12">
        <div className="flex space-x-2">
          <div className="h-3 w-3 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse"></div>
          <div className="h-3 w-3 bg-gradient-to-r from-accent to-primary rounded-full animate-pulse animation-delay-200"></div>
          <div className="h-3 w-3 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse animation-delay-400"></div>
        </div>
      </div>
    );
  }

  // Show access denied if user doesn't have permission
  if (hasPermission === false) {
    return (
      <div className="text-center py-12">
        <Settings2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
        <p className="text-muted-foreground">
          You don't have permission to manage features.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in">
      <div className="relative">
        <h1 className="admin-section-title mb-2">Features Management</h1>
        <p className="text-muted-foreground/80 text-lg">
          Manage features and assign them to subscription plans with automatic inheritance
        </p>
      </div>
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Subscription Features</h2>
          <p className="text-sm text-muted-foreground/80">Configure feature access for different subscription tiers</p>
        </div>
      </div>

      {/* Features Management Section */}
      <div className="space-y-4">
        <FeaturesManager communityId={currentCommunity.id} />
      </div>

      {/* Plan Features Management Section */}
      <div className="space-y-4">
        <Card className="admin-card admin-card-stats">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-green-600" />
              Plan Features Assignment
            </CardTitle>
            <CardDescription className="text-muted-foreground/80">
              Assign features to subscription plans. Plans inherit all features from lower-ranked plans automatically.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {plansLoading || hierarchyLoading ? (
              <div className="flex justify-center py-12">
                <div className="flex space-x-2">
                  <div className="h-3 w-3 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse"></div>
                  <div className="h-3 w-3 bg-gradient-to-r from-accent to-primary rounded-full animate-pulse animation-delay-200"></div>
                  <div className="h-3 w-3 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse animation-delay-400"></div>
                </div>
              </div>
            ) : (
              <>
                {plans && plans.length > 0 ? (
                  <div className="space-y-6">
                    {/* Feature Inheritance Explanation */}
                    <Card className="bg-blue-50/50 border-blue-200/60 admin-card-info">
                      <CardContent className="p-4 sm:p-6">
                        <div className="flex flex-col sm:flex-row items-start gap-3">
                          <TrendingUp className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="space-y-2">
                            <h4 className="font-semibold text-blue-800">Feature Inheritance</h4>
                            <p className="text-sm text-blue-700 leading-relaxed">
                              Plans are ranked from lowest (1) to highest. Higher-ranked plans automatically include all features from lower-ranked plans. 
                              This means if you assign a feature to Rank 1, it will be available in all plans.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Plan Hierarchy Display */}
                    {planHierarchy && (
                      <div className="space-y-4 sm:space-y-6">
                        <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                          <TrendingUp className="h-5 w-5 text-green-600" />
                          Plan Hierarchy & Features
                        </h3>
                        
                        {planHierarchy.map((planData, index) => {
                          const plan = plans.find(p => p.id === planData.id);
                          if (!plan) return null;

                          return (
                            <div key={plan.id}>
                              <Card className={`admin-card admin-card-stats border-l-4 border-l-primary/30 animate-fade-in stagger-${Math.min(index + 1, 5)}`}>
                                <CardHeader className="pb-3">
                                  <div className={`${isMobile ? 'space-y-4' : 'flex justify-between items-start'}`}>
                                    <div className="space-y-3">
                                      <div className="flex flex-wrap items-center gap-2">
                                        <CardTitle className="text-lg font-semibold text-slate-800">{plan.name}</CardTitle>
                                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                          Rank {plan.rank}
                                        </Badge>
                                        {plan.is_active ? (
                                          <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">Active</Badge>
                                        ) : (
                                          <Badge variant="secondary">Inactive</Badge>
                                        )}
                                        {plan.stripe_product_id && (
                                          <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                                            Stripe Connected
                                          </Badge>
                                        )}
                                      </div>
                                      
                                      {/* Feature Summary */}
                                      <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-sm text-muted-foreground/80">
                                        <span className="flex items-center gap-1">
                                          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                          {planData.direct_features.length} direct
                                        </span>
                                        {planData.inherited_features.length > 0 && (
                                          <span className="text-green-600 flex items-center gap-1">
                                            <ArrowDown className="h-3 w-3" />
                                            {planData.inherited_features.length} inherited
                                          </span>
                                        )}
                                        <span className="font-medium text-slate-700">
                                          Total: {planData.direct_features.length + planData.inherited_features.length}
                                        </span>
                                      </div>
                                    </div>
                                    <div className={`${isMobile ? 'w-full' : ''}`}>
                                      <PlanFeaturesManager
                                        planId={plan.id}
                                        planName={plan.name}
                                        communityId={currentCommunity.id}
                                      />
                                    </div>
                                  </div>
                                </CardHeader>
                                
                                {/* Feature Display */}
                                {(planData.direct_features.length > 0 || planData.inherited_features.length > 0) && (
                                  <CardContent className="pt-0 border-t border-gray-100/60">
                                    <div className="space-y-4 mt-4">
                                      {/* Direct Features */}
                                      {planData.direct_features.length > 0 && (
                                        <div className="space-y-2">
                                          <h5 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                                            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                            Direct Features
                                          </h5>
                                          <div className="flex flex-wrap gap-2">
                                            {planData.direct_features.map((feature) => (
                                              <Badge key={feature.id} variant="default" className="text-xs bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200 transition-colors">
                                                {feature.name}
                                              </Badge>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Inherited Features */}
                                      {planData.inherited_features.length > 0 && (
                                        <div className="space-y-2">
                                          <h5 className="text-sm font-semibold text-green-700 flex items-center gap-2">
                                            <ArrowDown className="h-3 w-3" />
                                            Inherited Features
                                          </h5>
                                          <div className="flex flex-wrap gap-2">
                                            {planData.inherited_features.map((feature) => (
                                              <Badge 
                                                key={feature.id} 
                                                variant="outline" 
                                                className="text-xs bg-green-50/80 text-green-700 border-green-200 hover:bg-green-100 transition-colors"
                                              >
                                                {feature.name}
                                                <span className="ml-1 text-green-600 text-[10px]">
                                                  (R{feature.inherited_from_rank})
                                                </span>
                                              </Badge>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </CardContent>
                                )}
                              </Card>
                              
                              {/* Inheritance Arrow */}
                              {index < planHierarchy.length - 1 && (
                                <div className="flex justify-center py-3 sm:py-4">
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground/70 bg-gray-50/50 px-4 py-2 rounded-full">
                                    <ArrowDown className="h-4 w-4" />
                                    <span className="hidden sm:inline">inherits from</span>
                                    <span className="sm:hidden">inherits</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">No subscription plans found</h3>
                    <p className="text-muted-foreground">
                      Create subscription plans first to assign features to them.
                    </p>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SubscriptionFeaturesManagement;
